import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { type IncidentDetail, type OpsIncidentRow } from '@vello/api-client';
import { AUTO_REFRESH_MS, useAutoRefresh, Badge, Button, Drawer, DrawerBody, DrawerFooter, DrawerHeader, EmptyState, KeyValueList, ListTable, Notice, PageHeader, Panel, Stack, TabPanel, Tabs, type Column, Text } from '@vello/web-theme';
import { formatMarketInstant, marketTimeZone, screenAllowed } from '../../src/index';
import { DrawerStack } from '../components/drawer-stack';
import { IdRef } from '../components/code-ref';
import { PersonName } from '../components/person-name';
import { ScopeMarketNotice, useScopeMarket } from '../components/scope-market';
import { useRouter } from '../router';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';

/**
 * Operações › Incidentes OPS (W7-OPS-GOVERNANCE, D420; the owner decision on D416, 2026-09-24) — the queue a driver's SOS
 * and trip reports land in (`GET /v1/ops/incidents?market=`, `ops:view` in the market; D416 routed them here, owner
 * decision D408 #6).
 *
 *   - **Read only.** The operations team calls the driver back and acts on the RIDE; the incident's status changes stay
 *     with SAFETY (`safety:manage`, the Segurança console). The screen offers no status control and says so.
 *   - **Fail closed on the market.** The queue is always one market's (the server answers `400 MARKET_REQUIRED` without
 *     one): the market comes from the scope selector or from the picker built out of the operator's own grants — never
 *     a typed id — and nothing is read until there is one.
 *   - **The row opens the incident** in a right-side drawer (the Directory pattern): the reason in words, who raised it and who
 *     it is about, severity, status, the ride, the reporter's location and the way to the Segurança console. What the server
 *     does not hold (a free-text report, the history of who changed the status) is said in words, never invented. The
 *     subject, evidence count and location are read from the incident detail (`GET /v1/safety/incidents/:id`), which
 *     needs `safety:view`; without it the drawer says so and still shows what the queue carries.
 *   - Oldest first (the server's order: the longest-waiting call first), times in the market's IANA time zone, and a
 *     re-read every 10 s (`useAutoRefresh`, visible tab only) — an SOS should not wait for a manual refresh.
 */
const SEVERITY_TONE: Readonly<Record<string, 'danger' | 'warning' | 'neutral'>> = { CRITICAL: 'danger', HIGH: 'danger', MEDIUM: 'warning', LOW: 'neutral' };

type DetailRead = { readonly id: string; readonly state: 'loading' } | { readonly id: string; readonly state: 'ok'; readonly detail: IncidentDetail } | { readonly id: string; readonly state: 'refused' } | { readonly id: string; readonly state: 'error' };
type DrawerTab = 'summary' | 'history';

export function OpsIncidentsScreen(): ReactElement {
  const { runtime, markets, grants } = useAdmin();
  const { navigate } = useRouter();
  const { t, lang } = useT();
  const { v, fail } = useVocab();

  const { market, choices } = useScopeMarket();
  const timeZone = marketTimeZone(market, markets);

  const [rows, setRows] = useState<readonly OpsIncidentRow[] | undefined>(undefined);
  const [listError, setListError] = useState<string | undefined>(undefined);
  const readSeq = useRef(0);
  const detailSeq = useRef(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState<DrawerTab>('summary');
  const [selected, setSelected] = useState<OpsIncidentRow | undefined>(undefined);
  const [detail, setDetail] = useState<DetailRead | undefined>(undefined);

  const load = useCallback(async (): Promise<void> => {
    const seq = ++readSeq.current;
    if (market === undefined) {
      setRows(undefined);
      setListError(undefined);
      return;
    }
    const res = await runtime.cases.listOpsIncidents({ market });
    if (seq !== readSeq.current) return;
    if (res.ok) {
      setRows(res.value.incidents);
      setListError(undefined);
    } else {
      setRows((prev) => prev ?? []);
      setListError(res.error.code === 'FORBIDDEN' ? t('w6.refused.forbidden') : t('w6.refused', { code: fail(res.error) }));
    }
  }, [runtime, market, t, fail]);

  useEffect(() => {
    setRows(undefined);
    void load();
  }, [load]);

  useAutoRefresh(load, { intervalMs: AUTO_REFRESH_MS.live, enabled: market !== undefined });

  const openIncident = (row: OpsIncidentRow): void => {
    const seq = ++detailSeq.current;
    setSelected(row);
    setDrawerTab('summary');
    setDrawerOpen(true);
    setDetail({ id: row.incidentId, state: 'loading' });
    void runtime.safety.getIncident(row.incidentId).then((res) => {
      if (seq !== detailSeq.current) return;
      if (res.ok) setDetail({ id: row.incidentId, state: 'ok', detail: res.value });
      else setDetail({ id: row.incidentId, state: res.error.code === 'FORBIDDEN' || res.error.code === 'NOT_FOUND' ? 'refused' : 'error' });
    });
  };

  // The open drawer follows the queue's fresh row; when the incident leaves the open queue it keeps the last row it had.
  const current = selected !== undefined ? (rows?.find((r) => r.incidentId === selected.incidentId) ?? selected) : undefined;

  const columns: readonly Column<OpsIncidentRow>[] = [
    { key: 'when', header: t('opsinc.col.when'), width: 'minmax(150px, 1fr)', render: (i) => <Text role="caption" tone="secondary" mono>{formatMarketInstant(i.createdAt, timeZone, lang)}</Text> },
    { key: 'reason', header: t('opsinc.col.reason'), width: 'minmax(170px, 1.2fr)', render: (i) => <strong>{v('ops.incident_reason', i.reasonCode)}</strong> },
    { key: 'severity', header: t('opsinc.col.severity'), width: 'minmax(110px, 0.8fr)', render: (i) => <Badge tone={SEVERITY_TONE[i.severity] ?? 'neutral'}>{v('safety.severity', i.severity)}</Badge> },
    { key: 'status', header: t('opsinc.col.status'), width: 'minmax(110px, 0.8fr)', render: (i) => <Text role="caption" tone="secondary">{v('safety.incident_status', i.status)}</Text> },
    { key: 'ride', header: t('opsinc.col.ride'), width: 'minmax(120px, 1fr)', render: (i) => (i.rideId !== undefined ? <IdRef value={i.rideId} /> : <Text role="caption" tone="secondary">{t('opsinc.noRide')}</Text>) },
    {
      key: 'reporter',
      header: t('opsinc.col.reporter'),
      width: 'minmax(150px, 1fr)',
      render: (i) => (
        <span>
          {v('identity.realm', i.reporter.role)} <Text role="caption" tone="secondary"><PersonName id={i.reporter.id} /></Text>
        </span>
      ),
    },
  ];

  const renderDrawer = (i: OpsIncidentRow): ReactElement => {
    const read = detail !== undefined && detail.id === i.incidentId ? detail : undefined;
    const d = read?.state === 'ok' ? read.detail : undefined;
    const loc = i.location ?? d?.location;
    const num = new Intl.NumberFormat(lang, { maximumFractionDigits: 5 });
    const notRead = t('opsinc.drawer.notRead');
    const canOpenSafety = screenAllowed(grants, 'cases');
    return (
      <>
        <DrawerHeader
          titleId="opsinc-drawer-title"
          title={v('ops.incident_reason', i.reasonCode)}
          subtitle={<span className="tnum">{formatMarketInstant(i.createdAt, timeZone, lang)}</span>}
          actions={<Badge tone={SEVERITY_TONE[i.severity] ?? 'neutral'}>{v('safety.severity', i.severity)}</Badge>}
          onClose={() => setDrawerOpen(false)}
          closeLabel={t('ui.close')}
        >
          <Tabs
            id="opsinc-drawer"
            ariaLabel={t('opsinc.drawer.tabs')}
            value={drawerTab}
            onChange={setDrawerTab}
            tabs={[
              { id: 'summary', label: t('opsinc.drawer.tab.summary') },
              { id: 'history', label: t('opsinc.drawer.tab.history') },
            ]}
          />
        </DrawerHeader>
        <DrawerBody>
          <DrawerStack>
            <TabPanel id="opsinc-drawer" tab="summary" value={drawerTab}>
              <Stack gap={4}>
                <KeyValueList
                  items={[
                    { label: t('opsinc.drawer.what'), value: v('ops.incident_reason', i.reasonCode) },
                    { label: t('opsinc.drawer.type'), value: v('safety.incident_type', i.type) },
                    { label: t('opsinc.col.severity'), value: v('safety.severity', i.severity) },
                    { label: t('opsinc.col.status'), value: v('safety.incident_status', i.status) },
                    { label: t('opsinc.col.reporter'), value: <span>{v('identity.realm', i.reporter.role)} · <PersonName id={i.reporter.id} /></span> },
                    {
                      label: t('opsinc.drawer.subject'),
                      value: d === undefined ? notRead : d.subjectId !== undefined ? <PersonName id={d.subjectId} /> : t('opsinc.drawer.subject.none'),
                    },
                    { label: t('opsinc.drawer.report'), value: t('opsinc.drawer.report.none') },
                    {
                      label: t('opsinc.col.ride'),
                      value: i.rideId !== undefined ? <IdRef value={i.rideId} copy full /> : t('opsinc.noRide'),
                    },
                    {
                      label: t('opsinc.drawer.location'),
                      value: loc !== undefined
                        ? <span className="tnum">{num.format(loc.latitude)}, {num.format(loc.longitude)}{loc.accuracyMeters !== undefined ? ` · ±${Math.round(loc.accuracyMeters)} m` : ''} · {v('safety.incident_location_source', loc.source)} · {formatMarketInstant(loc.capturedAt, timeZone, lang)}</span>
                        : t('opsinc.drawer.location.none'),
                    },
                    { label: t('opsinc.drawer.evidence'), value: d === undefined ? notRead : t('opsinc.drawer.evidence.count', { n: d.evidenceRefs.length }) },
                  ]}
                />
                {read?.state === 'loading' && <Text as="p" role="caption" tone="secondary">{t('ui.loading')}</Text>}
                {read?.state === 'refused' && <Notice tone="neutral">{t('opsinc.drawer.detail.refused')}</Notice>}
                {read?.state === 'error' && <Notice tone="warning">{t('opsinc.drawer.detail.error')}</Notice>}
              </Stack>
            </TabPanel>
            <TabPanel id="opsinc-drawer" tab="history" value={drawerTab}>
              <Stack gap={4}>
                <KeyValueList
                  items={[
                    { label: t('opsinc.drawer.history.raised'), value: formatMarketInstant(i.createdAt, timeZone, lang), mono: true },
                    { label: t('opsinc.drawer.history.status'), value: v('safety.incident_status', i.status) },
                    { label: t('opsinc.drawer.history.updated'), value: formatMarketInstant(i.updatedAt, timeZone, lang), mono: true },
                  ]}
                />
                <Notice tone="neutral">{t('opsinc.drawer.history.none')}</Notice>
              </Stack>
            </TabPanel>
          </DrawerStack>
        </DrawerBody>
        <DrawerFooter>
          {i.rideId !== undefined && <Button variant="primary" block icon="arrowRight" onClick={() => navigate('ride_detail', { id: i.rideId as string })}>{t('opsinc.drawer.openRide')}</Button>}
          {canOpenSafety ? <Button variant="secondary" block icon="arrowRight" onClick={() => navigate('cases')}>{t('opsinc.drawer.openSafety')}</Button> : <Notice tone="neutral">{t('opsinc.readOnly')}</Notice>}
        </DrawerFooter>
      </>
    );
  };

  return (
    <>
      <PageHeader
        title={t('opsinc.title')}
        subtitle={t('opsinc.subtitle')}
      />

      <Notice tone="neutral">{t('opsinc.readOnly')}</Notice>

      {market === undefined ? (
        <Panel>
          <ScopeMarketNotice choices={choices} />
        </Panel>
      ) : (
        <ListTable
          columns={columns}
          rows={rows ?? []}
          rowKey={(i) => i.incidentId}
          loading={rows === undefined}
          loadingLabel={t('ui.loading')}
          error={listError}
          onRetry={() => void load()}
          retryLabel={t('ui.retry')}
          empty={<EmptyState title={t('opsinc.empty.title')} detail={t('opsinc.empty.detail')} />}
          minWidth={860}
          onRowClick={openIncident}
          selectedKey={drawerOpen ? selected?.incidentId : undefined}
        />
      )}

      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} labelledBy="opsinc-drawer-title">
        {current !== undefined && renderDrawer(current)}
      </Drawer>
    </>
  );
}
